import { mediaQueryLarge, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

/** How long an animated row ([data-animate]) takes to open or close, in ms. */
const TOGGLE_DURATION = 250;

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** Rows that open and close with a height animation (blocks/_accordion-row.liquid). */
  get #animated() {
    return this.hasAttribute('data-animate');
  }

  #controller = new AbortController();

  /** @type {Animation | null} */
  #animation = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (!this.#animated || prefersReducedMotion()) return;

    event.preventDefault();
    // A row that is closing still has [open], so a tap while it closes opens it again.
    this.#toggle(!this.details.open || this.hasAttribute('data-closing'));
  };

  /**
   * Opens or closes the row with a height animation. Horizon animates rows in CSS with
   * interpolate-size, which iOS Safari doesn't support, so there they just pop open. This runs
   * anywhere: the content's height goes from 0 to its measured size (or back), with a fade.
   * Starts from wherever a running animation has got to, so a second tap reverses it smoothly.
   * @param {boolean} open
   */
  #toggle(open) {
    const { details } = this;
    const content = details.querySelector('.details-content');

    if (!(content instanceof HTMLElement)) {
      details.open = open;
      return;
    }

    const start = this.#contentBox(content, details.open);
    this.#animation?.cancel();
    this.#animation = null;

    if (open) {
      this.removeAttribute('data-closing');
      details.open = true;
      const end = this.#contentBox(content, true);

      const animation = content.animate([start, end], { duration: TOGGLE_DURATION, easing: 'ease-in-out' });
      this.#animation = animation;
      // Once it ends, the CSS for [open] (block-size: auto) takes over at the same size.
      animation.onfinish = () => {
        if (this.#animation === animation) this.#animation = null;
      };
      return;
    }

    // [open] comes off only once the content has closed, so it stays visible while it shrinks.
    this.setAttribute('data-closing', '');
    const end = { blockSize: '0px', opacity: '0', paddingBlockStart: '0px', paddingBlockEnd: '0px' };
    const animation = content.animate([start, end], {
      duration: TOGGLE_DURATION,
      easing: 'ease-in-out',
      fill: 'forwards',
    });
    this.#animation = animation;
    animation.onfinish = () => {
      if (this.#animation !== animation) return;
      details.open = false;
      this.removeAttribute('data-closing');
      animation.cancel();
      this.#animation = null;
    };
  }

  /**
   * The content's current height, opacity and block padding as keyframe values. The padding is
   * animated too: with border-box sizing the content can't get shorter than its padding.
   * @param {HTMLElement} content
   * @param {boolean} open - Whether the row is open; a closed row's content is collapsed to 0.
   */
  #contentBox(content, open) {
    if (!open) return { blockSize: '0px', opacity: '0', paddingBlockStart: '0px', paddingBlockEnd: '0px' };

    const style = getComputedStyle(content);
    return {
      blockSize: `${content.getBoundingClientRect().height}px`,
      opacity: style.opacity,
      paddingBlockStart: style.paddingBlockStart,
      paddingBlockEnd: style.paddingBlockEnd,
    };
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.#animation?.cancel();
    this.#animation = null;
    this.removeAttribute('data-closing');

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
